import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography
} from '@mui/material'
import { DataGridPro, GridColDef, GridRowsProp } from '@mui/x-data-grid-pro'
import { FC, useCallback, useContext, useMemo } from 'react'

import useAccessTokenAndShopId from '../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import { NotificationContext } from '../../providers'
import { useArchiveAutomatedVehicleReminderMutation } from '../../redux/api/vehicleApi'
import { AutomatedVehicleReminderEntry } from '../../sdk'
import { generateRruleProse } from '../../utils/generateRrule/generateRrule'

interface Props {
  entry: AutomatedVehicleReminderEntry
  handleClose: () => void
  handleSubmit: () => void
}

export const AutomatedSendReminderDeleteDialog: FC<Props> = ({
  entry,
  handleClose,
  handleSubmit,
  ...props
}) => {
  const { accessToken, shopId } = useAccessTokenAndShopId()
  const { setNotice } = useContext(NotificationContext)

  const [archiveAutomatedVehicleReminder] =
    useArchiveAutomatedVehicleReminderMutation()

  const columns = useMemo<GridColDef[]>(() => {
    return [
      {
        field: 'type',
        headerName: 'Type',
        flex: 1
      },
      {
        field: 'timeframe',
        headerName: 'Timeframe',
        flex: 1
      },
      {
        field: 'rrule',
        headerName: 'Frequency',
        flex: 3
      }
    ]
  }, [])

  const rows: GridRowsProp = useMemo(() => {
    return [
      {
        id: entry.id,
        type: entry.info?.type,
        timeframe: entry.info?.timeframe,
        rrule: entry.info?.rrule ? generateRruleProse(entry.info?.rrule) : ''
      }
    ]
  }, [entry])

  const handleDelete = useCallback(async () => {
    await archiveAutomatedVehicleReminder({
      authorization: accessToken,
      shop: shopId,
      id: entry.id!
    })

    setNotice('Successfully deleted automated vehicle reminder')

    handleSubmit()
  }, [
    accessToken,
    archiveAutomatedVehicleReminder,
    entry.id,
    handleSubmit,
    setNotice,
    shopId
  ])

  return (
    <Dialog {...props} open={true} onClose={handleClose}>
      <DialogTitle sx={{ fontWeight: 'bold' }}>
        Delete Automated Reminder
      </DialogTitle>
      <DialogContent sx={{ pb: 0 }}>
        <Typography pb={2}>
          Are you sure you want to delete this automated reminder?
        </Typography>

        <DataGridPro
          density="compact"
          rows={rows}
          columns={columns}
          autoHeight
        />
      </DialogContent>

      <DialogActions sx={{ pt: 2 }}>
        <Button variant="text" onClick={handleClose}>
          Back
        </Button>
        <Button variant="contained" onClick={handleDelete}>
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  )
}
